import { useState } from 'react'
import { CircleCheck, CirclePlay } from 'lucide-react'
import { formatDateTimeCompact, formatRelative } from '@/lib/datetime'
import { truncate } from '@/lib/format'
import { queryKeys } from '@/lib/query-client'
import { useAdminMutation } from '@/hooks/useAdminMutation'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { endTrip, startTrip } from './api'
import { useDueTrips, type DueTrip } from './useDueTrips'
import styles from './DueTripsAlert.module.css'

/**
 * The prompt an operator meets when a trip has passed its scheduled moment and
 * nobody has moved it on.
 *
 * Statuses go stale because nothing asks for them: a booking is approved days
 * ahead, and by the time the car actually pulls away the supervisor is looking
 * at a different screen. Every number built on trip status drifts from there.
 * So the queue itself raises it — the trips that are late to start or late to
 * finish, each with the one button that fixes it.
 *
 * Dismissing acknowledges the trips being shown, not the alert: a trip that
 * falls due afterwards opens it again, which is the point of a reminder that
 * arrives on its own.
 */
export function DueTripsAlert() {
  const { dueTrips } = useDueTrips()
  const [acknowledged, setAcknowledged] = useState<ReadonlySet<string>>(() => new Set())

  const outstanding = dueTrips.filter((due) => !acknowledged.has(due.key))

  const lifecycleMutation = useAdminMutation({
    mutationFn: ({ trip, action }: DueTrip) => (action === 'start' ? startTrip(trip.id) : endTrip(trip.id)),
    successTitle: 'Trip updated',
    errorTitle: 'Could not update the trip',
    // The booking behind the trip moves with it — a completed journey completes
    // its request — so the queue underneath this dialog is refreshed as well.
    invalidate: [
      queryKeys.trips.all,
      queryKeys.tripRequests.all,
      queryKeys.dashboard.all,
      queryKeys.drivers.all,
      queryKeys.changeLog.all,
    ],
  })

  // Acknowledges everything due at this moment, including anything that arrived
  // while the dialog was open and is not being looked at.
  const dismiss = () => setAcknowledged(new Set(dueTrips.map((due) => due.key)))

  const pendingKey = lifecycleMutation.isPending ? lifecycleMutation.variables?.key : undefined

  const single = outstanding.length === 1

  const title = single ? 'A trip needs its status updated' : `${outstanding.length} trips need their status updated`

  const description = single
    ? 'Its scheduled time has passed and it is still waiting. Starting or ending it records the actual time as now.'
    : 'Their scheduled times have passed and they are still waiting. Starting or ending one records the actual time as now.'

  return (
    <Dialog
      open={outstanding.length > 0}
      onClose={dismiss}
      busy={lifecycleMutation.isPending}
      size="md"
      title={title}
      description={description}
      footer={
        <Button variant="ghost" onClick={dismiss} disabled={lifecycleMutation.isPending}>
          Not now
        </Button>
      }
    >
      <ul className={styles.list}>
        {outstanding.map((due) => (
          <li key={due.key} className={styles.item}>
            <div className={styles.details}>
              <p className={styles.heading}>
                <span className={styles.reference}>{due.trip.bookingReference ?? `#${due.trip.id}`}</span>
                <StatusBadge status={due.trip.status} />
              </p>
              <p className={styles.people}>
                {due.trip.userName ?? 'Unnamed passenger'}
                {due.trip.driverName ? ` · ${due.trip.driverName}` : ' · no driver assigned'}
              </p>
              <p className={styles.when}>
                {due.action === 'start' ? 'Due to start' : 'Due to finish'} {formatDateTimeCompact(due.dueAt)}
                {' — '}
                <span className={styles.late}>{formatRelative(due.dueAt)}</span>
              </p>
              <p className={styles.route}>{truncate(due.trip.pickupAddress, 42, 'No pickup address')}</p>
            </div>

            <Button
              size="sm"
              className={styles.action}
              variant={due.action === 'start' ? 'primary' : 'danger'}
              iconLeft={
                due.action === 'start' ? <CirclePlay aria-hidden="true" /> : <CircleCheck aria-hidden="true" />
              }
              // One write at a time: the rest are disabled while one is in
              // flight, so a second click cannot land on a different trip
              // before the list has caught up with the first.
              loading={pendingKey === due.key}
              disabled={lifecycleMutation.isPending}
              onClick={() => lifecycleMutation.mutate(due)}
            >
              {due.action === 'start' ? 'Start trip' : 'End trip'}
            </Button>
          </li>
        ))}
      </ul>
    </Dialog>
  )
}
